import { useState } from "react";
import { Upload } from "lucide-react";
import api from "@/lib/api";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import ProfilePhotoCropper from "@/components/profile/ProfilePhotoCropper";

export default function ProfilePhotoField({ value, name, onChange, expert = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cropFile, setCropFile] = useState(null);

  const validateSelection = (file) => {
    if (!file) return;
    setError("");
    if (!["image/jpeg", "image/png", "image/webp", "image/avif"].includes(file.type)) {
      setError("Vælg et billede i JPG, PNG, WEBP eller AVIF-format.");
      return false;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Billedet må højst fylde 8 MB.");
      return false;
    }
    return true;
  };

  const selectFile = (file) => {
    if (!validateSelection(file)) return;
    if (expert) setCropFile(file);
    else upload(file, null);
  };

  const upload = async (file, crop) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("purpose", "profile_photo");
      if (crop) body.append("crop_json", JSON.stringify(crop));
      const { data } = await api.post("/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, data.crop || crop || null);
      setCropFile(null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Billedet kunne ikke uploades.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-outline p-5 sm:p-6" data-testid="profile-photo-field">
      <h2 className="font-display text-xl font-semibold text-brand-ink">Profilbillede</h2>
      <p className="mt-2 text-sm text-brand-body">
        {expert
          ? "Upload et tydeligt profilbillede. Dit ansigt skal være klart synligt, og billedet skal forestille dig. Undgå logoer, slørede billeder, gruppebilleder og billeder, hvor ansigtet ikke kan ses tydeligt."
          : "Tilføj et tydeligt billede, så eksperten kan genkende dig i forbindelse med din forespørgsel. Din kundeprofil er ikke offentlig."}
      </p>
      <p className="mt-1 text-xs text-brand-muted">{expert ? "Mindst 768 × 768 pixels." : "Mindst 400 × 400 pixels."} JPG, PNG, WebP eller AVIF. Maks. 8 MB.</p>
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <ProfileAvatar photoUrl={value} name={name} size="lg"/>
        <label className="card-outline px-4 py-3 cursor-pointer hover:bg-brand-soft">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,.avif"
            className="hidden"
            onChange={(event) => {
              selectFile(event.target.files?.[0]);
              event.target.value = "";
            }}
            disabled={busy}
          />
          <span className="flex items-center gap-2 text-sm font-medium text-brand-green">
            <Upload size={16}/> {busy ? "Uploader…" : value ? "Skift profilbillede" : "Upload profilbillede"}
          </span>
        </label>
        {value && (
          <button type="button" onClick={() => onChange("", null)} className="text-xs text-[#991B1B] hover:underline">
            Fjern billede
          </button>
        )}
      </div>
      {cropFile && <ProfilePhotoCropper
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onConfirm={({ file, crop }) => upload(file, crop)}
      />}
      {error && <div className="mt-2 text-xs text-[#991B1B]">{error}</div>}
    </section>
  );
}

